import { useState, useEffect } from "react";
import { FaStar } from "react-icons/fa";
import UserImg from "../../assets/home/user.jpg";
import { ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { HiUser } from "react-icons/hi2";

const AgentSkeleton = () => {
  return (
    <div className="w-full md:min-w-[250px] md:max-w-[260px] m-0 md:m-1 bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl animate-pulse">
      <div className="h-14 bg-gray-200 rounded-t-2xl"></div>

      <div className="flex flex-col items-center mt-6">
        <div className="w-20 h-20 bg-gray-200 rounded-full"></div>

        <div className="p-4 flex flex-col items-center gap-4 w-full">
          <div className="h-4 bg-gray-200 w-32 rounded"></div>
          <div className="h-3 bg-gray-200 w-24 rounded"></div>
          <div className="h-8 bg-gray-200 w-28 rounded-full mt-3"></div>
        </div>
      </div>
    </div>
  );
};

const AgentsEmptyState = () => (
  <div className="w-full md:col-span-2 lg:col-span-3 xl:col-span-3 bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[260px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F3F6F9] flex items-center justify-center mb-5 ring-1 ring-[#EAEAEA]"
      aria-hidden
    >
      <HiUser className="text-[#097FE6]" size={36} />
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      No featured agents yet
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      We don&apos;t have agents to highlight here at the moment. Explore the directory to connect with verified agents across Nigeria.
    </p>
    <Link to="/find-agents" className="w-full sm:w-auto">
      <button
        type="button"
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        Browse all agents
      </button>
    </Link>
  </div>
);

const AgentsErrorState = ({ onRetry }) => (
  <div className="w-full md:col-span-2 lg:col-span-3 xl:col-span-3 bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[260px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#FEF2F2] flex items-center justify-center mb-5 ring-1 ring-[#FECACA]"
      aria-hidden
    >
      <span className="text-2xl sm:text-3xl font-semibold text-[#DC2626]" aria-hidden>
        !
      </span>
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      Couldn&apos;t load agents
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      Something went wrong while fetching the list. Check your connection and try again.
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
    >
      Try again
    </button>
  </div>
);

const LocalAgents = ({
  agents: ClientSliderData = [],
  loading = false,
  fetchError = false,
  onRetry: loadAgents,
}) => {
  const [visibleCount, setVisibleCount] = useState(4);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width >= 1536) {
        setVisibleCount(4); // matches 2xl grid-cols-5
      } else if (width >= 1280) {
        setVisibleCount(4); // matches xl grid-cols-4
      } else if (width >= 1024) {
        setVisibleCount(3); // matches lg grid-cols-2
      } else {
        setVisibleCount(3); // mobile stacked
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="relative container py-10 sm:py-12 lg:py-16">
      <div className="mb-8 md:mb-12 flex flex-col sm:flex-row justify-between gap-4 sm:gap-6 items-start sm:items-center">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#000929]">
          Agents in Nigeria
        </h2>
        <Link to="/find-agents">
          <button className="hidden sm:flex text-[#097FE6] border-[1.5px] text-sm sm:text-base border-[#EAEAEA] rounded-full py-2 px-4 sm:px-5  justify-center items-center gap-1 sm:gap-2">
            <span>See All Agents</span>
            <ChevronRight size={18} />
          </button>
        </Link>
      </div>

      <div className="overflow-visible">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <AgentSkeleton key={index} />
            ))
          ) : (
            <>
              {fetchError ? (
                <AgentsErrorState onRetry={loadAgents} />
              ) : ClientSliderData.length === 0 ? (
                <AgentsEmptyState />
              ) : (
                ClientSliderData.slice(0, visibleCount).map((broker, index) => {
                  const avgRating =
                    broker?.reviews?.length > 0
                      ? broker.reviews.reduce((sum, item) => sum + Number(item.star), 0) /
                      broker.reviews.length
                      : 0;
                  return (
                    <AgentCardItem
                      key={broker?._id ?? `agent-${index}`}
                      broker={broker}
                      index={index}
                      avgRating={avgRating}
                    />
                  );
                })
              )}
              <div className="flex-1 bg-[#F3F6F9] border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col p-5 justify-between">
                <div className="w-14 h-14 bg-white rounded-full flex justify-center items-center">
                  <HiUser color="#097FE6" size={40} />
                </div>
                <div className="mt-10 mb-2">
                  <p className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">
                    Looking for more?
                  </p>
                  <p className="text-sm sm:text-base font-normal text-gray-500">
                    Search, compare and connect with more agents
                  </p>
                  <Link to="/find-agents">
                    <button className="w-full mt-4 rounded-full px-2 md:px-6 py-2.5 sm:px-8 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white">
                      Find more agents
                    </button>

                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AgentCardItem = ({ broker, index, avgRating }) => {
  const navigate = useNavigate();
  const headerBg = broker?.primaryColor || "#097FE6";
  const displayName = broker?.name?.trim() || "Agent";
  const jobLine = [broker?.job_title, broker?.agencyName].filter(Boolean).join(" / ") || "—";
  const reviewCount = broker?.reviews?.length ?? 0;

  return (
    <div className="flex-1 bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col">
      <div
        className="flex justify-center items-center text-white p-2 rounded-t-2xl font-semibold min-h-[48px]"
        style={{ backgroundColor: headerBg }}
      >
        {broker?.agencyLogo ? (
          <img
            src={broker.agencyLogo}
            alt=""
            className="h-8 object-contain max-w-[90%] cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/agency-profile/${broker?.agency_id}`);
            }}
          />
        ) : (
          <span className="text-xs sm:text-sm font-medium opacity-90 truncate px-2">
            {broker?.agencyName || "Agency"}
          </span>
        )}
      </div>

      <div className="flex flex-col items-center justify-center mt-6 flex-1">
        <img
          src={broker?.profileImg ? broker.profileImg : UserImg}
          alt=""
          className="w-20 h-20 rounded-full object-cover border-[4px] border-[#E9E9E9]"
        />

        <div className="p-4 flex flex-col items-center gap-4 sm:gap-6 w-full">
          <div className="text-center">
            <h3 className="text-base sm:text-lg mb-1 text-[#000929] capitalize font-medium">
              {displayName}
            </h3>
            <p className="text-gray-400 text-xs sm:text-sm">{jobLine}</p>
          </div>

          {reviewCount > 0 ? (
            <div className="text-xs sm:text-sm flex items-center gap-1">
              <FaStar size={14} color="#C2C823" className="-mt-1" aria-hidden />
              <p className="text-sm sm:text-base">
                {Number.isFinite(avgRating) ? avgRating.toFixed(1) : "0.0"}
              </p>
              <p>
                ({reviewCount} {reviewCount === 1 ? "Review" : "Reviews"})
              </p>
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-gray-500">No reviews yet</p>
          )}

          {broker?._id ? (
            <Link to={`/agent-profile/${broker._id}`} className="w-full flex justify-center">
              <button
                type="button"
                className="border-[1.5px] rounded-full text-xs sm:text-sm font-medium border-[#EAEAEA] px-6 sm:px-8 py-2 w-full sm:w-auto"
              >
                View Profile
              </button>
            </Link>
          ) : (
            <div className="w-full flex justify-center">
              <button
                type="button"
                disabled
                className="border-[1.5px] rounded-full text-xs sm:text-sm font-medium border-[#EAEAEA] text-gray-400 px-6 sm:px-8 py-2 w-full sm:w-auto cursor-not-allowed"
              >
                View Profile
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocalAgents;
